import apigClientFactory, { ApiGatewayClient } from "aws-api-gateway-client";
import { ClientConfig, HttpMethod, AdditionalParams, ApiGatewayClientResponse, WebsocketPresignedUrlResponse } from "../types/api";
import { ApiGatewayResponse } from "../types/api";
import errorManager from './errorManager';

interface CustomApiGatewayClient extends Omit<ApiGatewayClient, 'invokeApi'> {
  invokeApi(
    params: Record<string, unknown>,
    pathTemplate: string,
    method: HttpMethod,
    additionalParams?: {
      headers?: Record<string, unknown>;
      queryParams?: Record<string, string | number | boolean>;
      timeout?: number;
    },
    body?: Record<string, any>  // Changed from string to object
  ): Promise<ApiGatewayClientResponse>;
}


export abstract class BaseApiGatewayService {
  readonly clientConfig: ClientConfig;
  protected readonly apiGatewayClient: CustomApiGatewayClient;

  // Abstract methods - must be implemented by subclasses
  protected getPathPrefix(): string { return '/capigw/ai-playground/api'; }

  // Build headers in base class
  protected getRequestParams(): AdditionalParams {
    const headers: Record<string, unknown> = {
      'Content-Type': 'application/json'
    };
    return { headers };
  }

  constructor(invokeUrl: string, region: string) {
    try {
      const clientConfig: ClientConfig = {
        invokeUrl: invokeUrl,
        region: region,
        defaultContentType: 'application/json',
        defaultAcceptType: 'application/json'
      };
      this.clientConfig = clientConfig;
      this.apiGatewayClient = apigClientFactory.newClient(clientConfig) as CustomApiGatewayClient;
    } catch (error: any) {
      console.error("Error initializing API Gateway client:", error);
      throw new Error(error.message);
    }
  }



  async getHealth(): Promise<ApiGatewayResponse<{ user_id: string }>> {
    const params: Record<string, unknown> = {};
    const method: HttpMethod = 'GET';
    const pathTemplate = `${this.getPathPrefix()}/health`;

    try {
      const response = await this.apiGatewayClient.invokeApi(
        params,
        pathTemplate,
        method,
      ) as ApiGatewayClientResponse;

      return {
        statusCode: response.status,
        config: response.config,
        data: response.data,
        headers: response.headers
      };
    } catch (error: any) {
      // Extract status code from various possible locations
      let statusCode = 500;
      if (error?.response?.status) {
        statusCode = error.response.status;
      } else if (error?.status) {
        statusCode = error.status;
      } else if (error?.message === 'Network Error') {
        statusCode = 401; // Likely authentication failure
      }

      errorManager.showError('Health check failed. Service may be unavailable.');
      return {
        statusCode,
        config: {},
        data: {
          user_id: '',
        },
        headers: {}
      };
    }
  }

  async getWebsocketPresignedUrl(expires?: number): Promise<ApiGatewayResponse<WebsocketPresignedUrlResponse>> {
    const params: Record<string, unknown> = {};
    const method: HttpMethod = 'GET';
    let additionalParams = {
      ...this.getRequestParams(),
      queryParams: expires ? { "expires": expires } as Record<string, string | number> : {},
    };

    const pathTemplate = `${this.getPathPrefix()}/websocket/presigned-url`;

    console.debug('getWebsocketPresignedUrl::sending request to API Gateway:', {
      pathTemplate,
      method,
      queryParams: additionalParams.queryParams,
      headers: additionalParams.headers,
    });

    try {
      const response = await (this as any).apiGatewayClient.invokeApi(
        params,
        pathTemplate,
        method,
        additionalParams,
      ) as ApiGatewayClientResponse;

      console.log('getWebsocketPresignedUrl::received response from API Gateway');

      return {
        statusCode: response.status,
        config: response.config,
        data: response.data,
        headers: response.headers
      };
    } catch (error: any) {
      console.error('Error in getWebsocketPresignedUrl request:', error);
      errorManager.showError('Failed to establish connection. Please refresh the page.');
      return {
        statusCode: error?.response?.status || 500,
        config: {},
        data: {
          websocket_presigned_url: '',
        },
        headers: {}
      };
    }
  }
}